/* eslint-disable @angular-eslint/no-input-rename */
import {
    Component,
    Input,
    ChangeDetectionStrategy,
    HostListener,
    NgZone,
    Output,
    EventEmitter,
    OnInit,
} from '@angular/core';
import { sortBy, uniqBy, groupBy } from 'lodash-es';

import {
    Player,
    PlayerStatistics,
    StatsTournament,
    StatsTournamentSeason,
    StatsTournamentStatsKey,
    StatsTournamentStatsSubtournament,
} from '@interfaces/match-data.interface';

import SPORT_CONFIG from '@config/sport.config';

import $ from '@shared/jquery';
import { BaseComponent } from '@components/base.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { StatGroup, StatGroupType } from '@interfaces/participant.interface';

interface PlayerTournament {
    name: string;
    id: number;
}

interface PlayerSubtournament {
    name: string;
    id: number;
    type: string | null;
    order: number;
}

@Component({
    selector: 'app-players-v2',
    templateUrl: './players-v2.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayersV2Component extends BaseComponent implements OnInit {
    @Input()
    public id?: number;

    @Input()
    public home: string;

    @Input()
    public away: string;

    @Input()
    public isNationalTeam: boolean = false;

    @Input('formation_home')
    public formationHome: string;

    @Input('formation_away')
    public formationAway: string;

    @Input()
    public statistics: PlayerStatistics | null = null;

    @Input()
    public sportName: string;

    @Input()
    public sportId: number;

    @Input()
    public seasonId: number;

    @Input()
    public hideSeasonSelect: boolean = false;

    @Input()
    public betradarMajorLeague: boolean = false;

    @Output()
    refresh = new EventEmitter<number | null>();

    public subHome: Player[];
    public subAway: Player[];
    public players: { [prop: string]: Player[] };
    public positionCss: Record<string, string> = {};
    private dataVal: Player[];
    private playerSelectedVal: number | null = null;

    public selectedStatsSeasonId: { [key: number]: number } = {};
    public selectedStatsSubtournamentId: number | null = null;
    public selectedStatsGroup: StatGroupType = 'defense';
    selectedStatsGroupId: number = 0;

    public console = console;

    public emph: string;

    public constructor(
        private deviceService: DeviceDetectorService,
        private ngZone: NgZone
    ) {
        super();
    }

    get data(): any {
        return this.dataVal;
    }

    @Input()
    set data(v: any) {
        v = v
            .map((val: Player) => {
                val.sort = 100;
                if (
                    val.position === 'goalkeeper' ||
                    val.position?.toLowerCase() === 'g' ||
                    val.position?.toLowerCase() === 'goalie' ||
                    val.position?.toLowerCase() === 'pr'
                ) {
                    val.sort = 1;
                } else if (
                    val.position === 'defender' ||
                    val.position?.toLowerCase() === 'd' ||
                    val.position?.toLowerCase() === 'right back' ||
                    val.position?.toLowerCase() === 'left back' ||
                    val.position?.toLowerCase() === 'ho' ||
                    val.position?.toLowerCase() === 'g-f'
                ) {
                    val.sort = 2;
                } else if (
                    val.position === 'midfielder' ||
                    val.position?.toLowerCase() === 'm' ||
                    val.position?.toLowerCase() === 'left wink' ||
                    val.position?.toLowerCase() === 'l' ||
                    val.position?.toLowerCase() === 'c-f' ||
                    val.position?.toLowerCase() === 'hb'
                ) {
                    val.sort = 3;
                } else if (
                    val.position === 'forward' ||
                    val.position?.toLowerCase() === 'f' ||
                    val.position?.toLowerCase() === 'right wink' ||
                    val.position?.toLowerCase() === 'br' ||
                    val.position?.toLowerCase() === 'uf'
                ) {
                    val.sort = 4;
                } else if (
                    val.position?.toLowerCase() === 'sh' ||
                    val.position?.toLowerCase() === 'center' ||
                    val.position?.toLowerCase() === 'fb'
                ) {
                    val.sort = 5;
                } else if (val.position?.toLowerCase() === 'fh') {
                    val.sort = 6;
                } else if (val.position?.toLowerCase() === 'c') {
                    val.sort = 7;
                } else if (val.position?.toLowerCase() === 'w') {
                    val.sort = 8;
                } else if (val.position?.toLowerCase() === 'fl') {
                    val.sort = 9;
                } else if (val.position?.toLowerCase() === 'ub') {
                    val.sort = 9;
                }

                return val;
            })
            .filter((val: Player) => !val.role?.includes('-duplicity'));
        this.dataVal = sortBy(v, ['sort', 'position']);
    }

    public ngOnInit(): void {
        if (this.isBasketballSport(this.sportName)) {
            this.selectedStatsGroup = 'points';
        }
        if (this.isBaseballSport(this.sportName)) {
            this.selectedStatsGroup = 'hitting';
        }
        this.ressetStatsGroup();
    }

    private ressetStatsGroup(): void {
        this.getStatGroups().forEach((val, index) => {
            if (val === this.selectedStatsGroup) {
                this.selectedStatsGroupId = index;
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public _isHome: boolean = true;

    public tooltip(): void {
        setTimeout(() => {
            $('[data-toggle="tooltip"]').tooltip();
        }, 300);
    }

    /**
     * Is hpme tab active
     * @return {boolean}
     */
    public get isHome(): boolean {
        return this._isHome;
    }

    get hasCards(): boolean {
        return SPORT_CONFIG.playerStats.redYellowCard.includes(this.sportName);
    }

    get playerTournament(): PlayerTournament[] {
        let tournaments: PlayerTournament[] = [];
        this.data.forEach((player: Player) => {
            tournaments = [
                ...tournaments,
                ...player.statsTournament!.map((val: StatsTournament) => ({
                    id: val.tournament_id,
                    name: val.tournament_name,
                    order: val.tournamentOrder,
                })),
            ];
        });

        return sortBy(uniqBy(tournaments, 'id'), 'order');
    }

    playerSeason(tournamentId: number): StatsTournamentSeason[] {
        if (tournamentId === 0 || this.data.length === 0) {
            return [];
        }

        const tournament = this.data[0].statsTournament.find(
            (statsTournament: StatsTournament) => statsTournament.tournament_id === tournamentId
        );

        return tournament ? tournament.seasons : [];
    }

    playerSubtournament(tournamentId: number): PlayerSubtournament[] {
        const subtournaments: PlayerSubtournament[] = [];

        this.data.forEach((player: Player) => {
            player.statsTournament?.forEach((statTournament: StatsTournament) => {
                if (statTournament.tournament_id === tournamentId && statTournament.subtournaments !== undefined) {
                    statTournament.subtournaments.forEach((subtournament) => {
                        subtournaments.push({
                            id: subtournament.subtournament_id,
                            name: subtournament.subtournament_name,
                            type: subtournament.subtournament_type,
                            order: subtournament.subtournament_order ?? 0,
                        });
                    });
                }
            });
        });

        return sortBy(uniqBy(subtournaments, 'id'), 'order');
    }

    get colspan(): number {
        let x = 5;
        if (this.showMatchPlayed) {
            x += 1;
        }
        if (this.showStats) {
            x += 1;
        }
        if (this.hasAssists) {
            x += 1;
        }
        if (this.hasCards) {
            x += 2;
        }
        if (this.hasAmericanFootballStats) {
            const statGroup = this.americanFootballStats.find((group) => group.group === this.selectedStatsGroup);
            if (statGroup !== undefined) {
                x += statGroup.stats.length;
            }
        }

        if (this.hasBasketballStats) {
            const statGroup = this.basketballStats.find((group) => group.group === this.selectedStatsGroup);
            if (statGroup !== undefined) {
                x += statGroup.stats.length;
            }
        }

        if (this.hasBaseballStats) {
            const statGroup = this.baseballStats.find((group) => group.group === this.selectedStatsGroup);
            if (statGroup !== undefined) {
                x += statGroup.stats.length;
            }
        }

        // Major leagues has Injury tab, Injury column is hidden
        if (this.betradarMajorLeague) {
            x -= 1;
        }

        return x;
    }

    get playerSelectedT(): number | undefined {
        if (this.playerSelectedVal === null) {
            return this.playerTournament[0]?.id;
        }
        return this.playerSelectedVal;
    }

    get playerSelectedSeason(): number | undefined {
        const selectedTournamentId = this.playerSelectedT;

        if (!selectedTournamentId) {
            return undefined;
        }

        const selectedSeasonId = this.selectedStatsSeasonId[selectedTournamentId];
        if (selectedSeasonId) {
            return selectedSeasonId;
        }

        const selectedSeason = this.playerSeason(selectedTournamentId);

        return selectedSeason?.[0]?.season_id;
    }

    get finalData(): Record<string, Player[]> {
        return groupBy(this.data, 'position') as Record<string, Player[]>;
    }

    get showMatchPlayed(): boolean {
        return this.isSoccer(this.sportName);
    }

    get showInjuryIcon(): boolean {
        return this.isSoccer(this.sportName) && !this.betradarMajorLeague;
    }

    get showStats(): boolean {
        return this.isHockey(this.sportName) || this.isSoccer(this.sportName) || this.isHandball(this.sportName);
    }

    get hasAssists(): boolean {
        return this.isHockey(this.sportName) || this.isSoccer(this.sportName) || this.isHandball(this.sportName);
    }

    get hasAmericanFootballStats(): boolean {
        return this.isAmericanFootball(this.sportName);
    }

    get hasBasketballStats(): boolean {
        return this.isBasketballSport(this.sportName);
    }

    get hasBaseballStats(): boolean {
        return this.isBaseballSport(this.sportName);
    }

    get americanFootballStats(): StatGroup[] {
        return [
            {
                group: 'defense',
                stats: [
                    'defense_tackles',
                    'defense_assists',
                    'defense_sacks',
                    'defense_interceptions',
                    'defense_forced_fumbles',
                ],
            },
            {
                group: 'offense',
                stats: [
                    'passing_completions',
                    'passing_attempts',
                    'passing_yards',
                    'passing_touchdowns',
                    'passing_interceptions',
                    'rushing_attempts',
                    'rushing_yards',
                    'rushing_touchdowns',
                    'rushing_longest',
                    'receiving_targets',
                    'receiving_receptions',
                    'receiving_yards',
                    'receiving_touchdowns',
                    'receiving_longest',
                ],
            },
            {
                group: 'special_teams',
                stats: [
                    'field_goals_made',
                    'field_goals_attempts',
                    'field_goals_longest',
                    'extra_points_made',
                    'extra_points_attempts',
                    'punt_returns_returns',
                    'punts_attempts',
                    'punts_avg_yards',
                    'punts_inside_20',
                    'punts_longest',
                    'kick_returns_returns',
                    'kick_returns_avg_yards',
                    'kick_returns_longest',
                    'kick_returns_touchdowns',
                    'punt_returns_avg_yards',
                    'punt_returns_longest',
                ],
            },
        ];
    }

    get basketballStats(): StatGroup[] {
        return [
            {
                group: 'points',
                stats: [
                    'total_points',
                    'total_points_off_turnovers',
                    'total_fast_break_pts',
                    'total_second_chance_pts',
                    'total_points_in_paint',
                    'total_points_in_paint_made',
                    'total_points_in_paint_att',
                    'total_points_in_paint_pct',
                    'average_points',
                    'average_points_off_turnovers',
                    'average_fast_break_pts',
                    'average_second_chance_pts',
                    'average_points_in_paint_made',
                    'average_points_in_paint',
                    'average_points_in_paint_att',
                ],
            },
            {
                group: 'rebounds',
                stats: [
                    'total_rebounds',
                    'total_defensive_rebounds',
                    'total_offensive_rebounds',
                    'average_rebounds',
                    'average_def_rebounds',
                    'average_off_rebounds',
                ],
            },
            {
                group: 'assists_and_turnovers',
                stats: [
                    'total_assists',
                    'average_assists',
                    'total_assists_turnover_ratio',
                    'average_turnovers',
                    'total_turnovers',
                    'total_usage_pct',
                ],
            },
            {
                group: 'defense',
                stats: [
                    'average_steals',
                    'total_steals',
                    'average_blocks',
                    'total_blocks',
                    'average_blocked_att',
                    'total_blocked_att',
                ],
            },
            {
                group: 'shooting',
                stats: [
                    'total_field_goals_made',
                    'total_field_goals_att',
                    'total_field_goals_pct',
                    'total_true_shooting_pct',
                    'total_effective_fg_pct',
                    'total_three_points_made',
                    'total_three_points_att',
                    'total_three_points_pct',
                    'total_two_points_made',
                    'total_two_points_att',
                    'total_two_points_pct',
                    'total_free_throws_made',
                    'total_free_throws_att',
                    'total_free_throws_pct',
                    'total_true_shooting_att',
                    'average_field_goals_att',
                    'average_field_goals_made',
                    'average_two_points_att',
                    'average_two_points_made',
                    'average_three_points_att',
                    'average_three_points_made',
                    'average_free_throws_att',
                    'average_free_throws_made',
                    'average_true_shooting_att',
                ],
            },
        ];
    }

    get baseballStats(): StatGroup[] {
        return [
            {
                group: 'hitting',
                stats: [
                    'hitting_overall_wrc_plus',
                    'hitting_overall_ops',
                    'hitting_overall_obp',
                    'hitting_overall_slg',
                    'hitting_overall_woba',
                    'hitting_overall_iso',
                    'hitting_overall_bbpa',
                    'hitting_overall_pitches_ktotal',
                    'hitting_overall_babip',
                ],
            },
            {
                group: 'pitching',
                stats: [
                    'pitching_overall_fip',
                    'pitching_overall_xfip',
                    'pitching_overall_whip',
                    'pitching_overall_kbb',
                    'pitching_overall_k9',
                    'pitching_overall_onbase_h9',
                    'pitching_overall_obp',
                    'pitching_overall_babip',
                ],
            },
            {
                group: 'fielding',
                stats: [
                    'fielding_overall_fpct',
                    'fielding_overall_error',
                    'fielding_overall_rf',
                    'fielding_overall_dp',
                    'fielding_overall_po',
                    'fielding_overall_a',
                    'fielding_overall_tc',
                    'fielding_overall_steal_caught',
                    'fielding_overall_steal_stolen',
                    'fielding_overall_pb',
                ],
            },
        ];
    }

    public over(p: number, side: string = 'home'): void {
        $('.app-players-field').addClass('d-none');
        $(`#p_${side}_${p}`).removeClass('d-none');
    }

    public out(): void {
        $('.app-players-field').addClass('d-none');
    }

    public trackByFn(index: number): number {
        return index;
    }

    public activePlayerT(id: number): void {
        this.playerSelectedVal = id;

        delete this.selectedStatsSeasonId[id];
        this.refresh.emit(null);
    }

    public playerData(stats: StatsTournament[], type: StatsTournamentStatsKey): number | string {
        const tournament = stats.find((val: StatsTournament) => val.tournament_id === this.playerSelectedT);

        if (this.playerSelectedT === 0) {
            let total = 0;
            stats.forEach((statsTournament: StatsTournament) => {
                let val: any = 0;
                if (statsTournament.has_subtournaments && statsTournament.subtournaments !== undefined) {
                    statsTournament.subtournaments.forEach((st: StatsTournamentStatsSubtournament) => {
                        val += +(st.data[type]?.value ?? 0);
                    });
                } else if (statsTournament.data !== undefined) {
                    val = +(statsTournament.data[type]?.value ?? 0);
                }

                total += +val;
            });

            return total || '-';
        }

        let value: string | undefined;

        if (tournament?.subtournaments !== undefined) {
            const subtournament =
                tournament.subtournaments.find(
                    // eslint-disable-next-line max-len
                    (val: StatsTournamentStatsSubtournament) =>
                        val.subtournament_id === this.getSelectedStatsSubtournamentId()
                ) ?? null;

            if (!subtournament) {
                return '-';
            }

            value = subtournament.data[type]?.value ?? '-';
        } else {
            value = tournament?.data?.[type]?.value ?? '-';
        }

        if (value === '0') {
            return '-';
        }
        return value ?? '-';
    }

    @HostListener('window:scroll', ['$event'])
    public onScroll(): void {
        this.ngZone.runOutsideAngular((): void => {
            const el = document.getElementById('players-squad');
            if (!el) {
                return;
            }
            const el2 = document.getElementById('players-squad-virtual');
            const rect = el!.getBoundingClientRect();

            if (rect.y < 0) {
                let num = Math.abs(rect.y);
                if (this.deviceService.isMobile()) {
                    num += 60;
                }

                el2!.style.top = `${num}px`;
            } else {
                el2!.style.top = '0px';
            }
        });
    }

    public getStatGroups(): StatGroupType[] {
        if (this.isAmericanFootball(this.sportName)) {
            return ['defense', 'offense', 'special_teams'];
        }
        if (this.isBasketballSport(this.sportName)) {
            return ['points', 'rebounds', 'assists_and_turnovers', 'defense', 'shooting'];
        }
        if (this.isBaseball(this.sportName)) {
            return ['hitting', 'pitching', 'fielding'];
        }

        return [];
    }

    public emphColumn(column: string): void {
        this.emph = column;
    }

    public onStatSeasonChange(e: Event): void {
        if (!this.playerSelectedT) {
            return;
        }

        const selectedStatsSeasonId = +(e.target as HTMLInputElement).value;

        this.selectedStatsSeasonId[this.playerSelectedT] = selectedStatsSeasonId;
        this.selectedStatsSubtournamentId = null;
        this.selectedStatsGroup = 'defense';
        if (this.isBasketballSport(this.sportName)) {
            this.selectedStatsGroup = 'points';
        }
        if (this.isBaseballSport(this.sportName)) {
            this.selectedStatsGroup = 'hitting';
        }
        this.ressetStatsGroup();

        this.refresh.emit(selectedStatsSeasonId);
        this.tooltip();
    }

    public onStatSubtournamentChange(e: Event): void {
        this.selectedStatsSubtournamentId = +(e.target as HTMLInputElement).value;
        this.selectedStatsGroup = 'defense';
        if (this.isBasketballSport(this.sportName)) {
            this.selectedStatsGroup = 'points';
        }
        if (this.isBaseballSport(this.sportName)) {
            this.selectedStatsGroup = 'hitting';
        }
        this.ressetStatsGroup();
        this.tooltip();
    }

    public onStatGroupChange(e: Event): void {
        const index = +(e.target as HTMLInputElement).value;

        this.selectedStatsGroup = this.getStatGroups()[index]!;
        this.selectedStatsGroupId = +index;
        this.tooltip();
    }

    public trackByFnSeason(index: number, item: StatsTournamentSeason): number {
        return item.season_id;
    }

    public trackByFnSubtournament(index: number, item: PlayerSubtournament): number {
        return item.id;
    }

    public getSelectedStatsSubtournamentId(): number | null {
        const subtournaments = this.playerSubtournament(this.playerSelectedT ?? 0);
        if (this.selectedStatsSubtournamentId === null) {
            const regSubtournament = subtournaments.find((st) => st.type === 'REG');
            return regSubtournament?.id ?? subtournaments?.[0]?.id ?? null;
        }

        const selected = subtournaments.find((st) => st.id === this.selectedStatsSubtournamentId);

        return selected?.id ?? null;
    }
}
